export function cleanupHtmlForSave(html: string): string {
	const parser = new DOMParser();

	// --- Helpers ----------------------------------------------------------------
	const removeNode = (n: Node | null) => {
		if (n && n.parentNode) n.parentNode.removeChild(n);
	};

	const isIgnorableWhitespace = (n: Node | null) =>
		!!n && n.nodeType === Node.TEXT_NODE && n.textContent!.trim() === '';

	const isEmptyParagraph = (n: Node | null) => {
		if (!n || n.nodeType !== Node.ELEMENT_NODE) return false;
		const el = n as HTMLElement;
		if (el.tagName !== 'P') return false;
		const txt = el.textContent?.trim() ?? '';
		const onlyBr =
			el.childNodes.length === 1 &&
			el.firstChild?.nodeType === Node.ELEMENT_NODE &&
			(el.firstChild as Element).tagName === 'BR';
		return txt === '' || onlyBr;
	};

	const isEditor = (n: Node | null) => {
		if (!n || n.nodeType !== Node.ELEMENT_NODE) return false;
		const el = n as HTMLElement;
		return el.tagName === 'DIV' && el.hasAttribute('contenteditable') && el.classList.contains('ProseMirror');
	};

	const unwrapEditorInto = (target: HTMLElement, editor: HTMLElement) => {
		while (editor.firstChild) target.appendChild(editor.firstChild);
		removeNode(editor);
	};

	const removeIfSeparator = (n: Node | null) => {
		if (isIgnorableWhitespace(n)) removeNode(n);
	};

	// Content check used in Phase 3
	const VOID_CONTENT_TAGS = new Set([
		'IMG',
		'HR',
		'IFRAME',
		'VIDEO',
		'AUDIO',
		'EMBED',
		'OBJECT',
		'CANVAS',
		'SVG',
		'MATH',
	]);

	const nodeHasMeaningfulContent = (n: Node): boolean => {
		if (n.nodeType === Node.TEXT_NODE) {
			return (n.textContent?.trim().length ?? 0) > 0;
		}
		if (n.nodeType === Node.ELEMENT_NODE) {
			const el = n as HTMLElement;

			// Ignore <br>
			if (el.tagName === 'BR') return false;

			// Void/visual elements count as content
			if (VOID_CONTENT_TAGS.has(el.tagName)) return true;

			// <p> that is effectively empty does not count
			// if (el.tagName === 'P' && isEmptyParagraph(el)) return false;

			// Otherwise, check children
			if (el.childNodes && el.childNodes.length > 0) {
				for (const i of el.childNodes) {
					if (nodeHasMeaningfulContent(i)) return true;
				}
				return false;
			}

			// Empty non-void element (like empty <div>) → no content
			return false;
		}
		return false;
	};

	const elementHasMeaningfulContent = (el: HTMLElement): boolean => {
		// Quick exit: any meaningful child?
		for (const i of el.childNodes) {
			if (nodeHasMeaningfulContent(i)) return true;
		}
		return false;
	};

	// --- Phase 1: unwrap editors inside existing [data-type="user-interactive"] ----
	const doc1 = parser.parseFromString(html, 'text/html');
	const interactiveNodes = doc1.querySelectorAll<HTMLElement>(
		'[data-type="user-interactive"], [data-type="user-interactive-fds"]',
	);

	interactiveNodes.forEach((container) => {
		const editors = container.querySelectorAll<HTMLElement>('div.ProseMirror[contenteditable]');
		editors.forEach((ed) => unwrapEditorInto(container, ed));
		Array.from(container.childNodes).forEach((n) => removeIfSeparator(n));
	});

	// Serialize stage 1
	let cleanedHtml = new XMLSerializer().serializeToString(doc1);

	// --- Phase 2: merge standalone editors per parent using index-based scan ----
	const doc2 = parser.parseFromString(cleanedHtml, 'text/html');
	const parents = doc2.querySelectorAll<HTMLElement>('body, body *');

	parents.forEach((parent) => {
		if (!parent.childNodes || parent.childNodes.length === 0) return;
		let i = 0;

		while (i < parent.childNodes.length) {
			const node = parent.childNodes[i];
			if (!isEditor(node)) {
				i++;
				continue;
			}

			// Contiguous run (editors + empty <p> / whitespace)
			let j = i;
			let sawEditor = false;
			while (j < parent.childNodes.length) {
				const n = parent.childNodes[j];
				if (isEditor(n)) {
					sawEditor = true;
					j++;
					continue;
				}
				if (isEmptyParagraph(n) || isIgnorableWhitespace(n)) {
					j++;
					continue;
				}
				break;
			}

			const dataTypeVal = (parent as HTMLElement).getAttribute('data-type');
			const parentIsInteractive = dataTypeVal === 'user-interactive' || dataTypeVal === 'user-interactive-fds';

			if (sawEditor) {
				let targetWrapper: HTMLElement;
				if (parentIsInteractive) {
					targetWrapper = parent as HTMLElement;
				} else {
					targetWrapper = doc2.createElement('div');
					targetWrapper.setAttribute('data-type', 'user-interactive');
					parent.insertBefore(targetWrapper, parent.childNodes[i]);
				}

				// Consume the run [i..j)
				while (i < j) {
					const current = parent.childNodes[i];
					if (isEditor(current)) {
						unwrapEditorInto(targetWrapper, current as HTMLElement);
						j--;
					} else if (isEmptyParagraph(current) || isIgnorableWhitespace(current)) {
						removeNode(current);
						j--;
					} else {
						i++;
					}
				}

				if (!parentIsInteractive) {
					i = Array.prototype.indexOf.call(parent.childNodes, targetWrapper) + 1;
				}
			} else {
				i++;
			}
		}
	});

	cleanedHtml = new XMLSerializer().serializeToString(doc2);

	// --- Phase 3: remove empty interactive islands that have a sibling with content ----
	const doc3 = parser.parseFromString(cleanedHtml, 'text/html');
	const allIslands = Array.from(doc3.querySelectorAll<HTMLElement>('[data-type="user-interactive"]'));

	// Group islands by parent to evaluate siblings
	const byParent = new Map<ParentNode, HTMLElement[]>();
	allIslands.forEach((el) => {
		const parent = el.parentNode!;
		const list = byParent.get(parent) || [];
		list.push(el);
		byParent.set(parent, list);
	});

	byParent.forEach((islands) => {
		// Determine which of these siblings have content
		const withContent = islands.filter((el) => elementHasMeaningfulContent(el));
		if (withContent.length === 0) return; // no content anywhere; keep empties

		// Remove any empty island if there exists at least one sibling with content
		islands.forEach((el) => {
			if (!elementHasMeaningfulContent(el)) {
				removeNode(el);
			}
		});
	});

	// --- Phase 4: strip whitespace-only text nodes inside interactive islands --------
	// XMLSerializer / DOMParser round-trips leave whitespace text nodes between
	// block elements. Tiptap's ProseMirror parser converts those into extra empty
	// <p> tags, causing blank lines to accumulate across save/reload cycles.
	const remainingIslands = doc3.querySelectorAll<HTMLElement>(
		'[data-type="user-interactive"], [data-type="user-interactive-fds"]',
	);
	remainingIslands.forEach((island) => {
		const toRemove: Node[] = [];
		for (const node of island.childNodes) {
			if (isIgnorableWhitespace(node)) {
				toRemove.push(node);
			}
		}
		toRemove.forEach((n) => removeNode(n));
	});

	cleanedHtml = new XMLSerializer().serializeToString(doc3);
	return cleanedHtml;
}

/**
 * This function uses the original template HTML from the server with the server replacement marks and the empty data-type="user-interactive" elements and merges it with the edited HTML from the client-side editor.
 * Target is to have intact serverside template only with the filled data-type="user-interactive" elements from the edited HTML.
 * @param originalTemplateHtml
 * @param editedHtml
 */
export function mergeOriginalTemplateWithEditedContent(originalTemplateHtml: string, editedHtml: string): string {
	const parser = new DOMParser();
	const originalDoc = parser.parseFromString(originalTemplateHtml, 'text/html');
	const editedDoc = parser.parseFromString(editedHtml, 'text/html');

	const islandSelector = '[data-type="user-interactive"], [data-type="user-interactive-fds"]';
	const originalIslands = originalDoc.querySelectorAll<HTMLElement>(islandSelector);
	const editedIslands = editedDoc.querySelectorAll<HTMLElement>(islandSelector);

	if (originalIslands.length !== editedIslands.length) {
		console.warn('Number of interactive islands in original template and edited content do not match.');
	}

	const count = Math.min(originalIslands.length, editedIslands.length);
	for (let i = 0; i < count; i++) {
		const originalIsland = originalIslands[i];
		const editedIsland = editedIslands[i];

		// Replace the content of the original island with the content from the edited island
		originalIsland.innerHTML = cleanEditedIslandHtml(editedIsland.innerHTML);
	}

	const mergedHtml = new XMLSerializer().serializeToString(originalDoc);
	return mergedHtml;
}

/**
 * Cleans legacy editor artifacts from interactive template content.
 * Edge <br> elements are removed because they were unintentionally accumulated
 * during repeated edit/save cycles, while intentional line breaks inside content are preserved.
 */
function cleanEditedIslandHtml(html: string): string {
	const temp = document.createElement('div');
	temp.innerHTML = html;

	const editorRoot = temp.querySelector<HTMLElement>('.ProseMirror');
	const contentRoot = editorRoot ?? temp;

	// Remove editor-specific trailing breaks
	contentRoot.querySelectorAll('.ProseMirror-trailingBreak').forEach((el) => el.remove());

	// Remove legacy line breaks at the beginning/end of paragraphs
	contentRoot.querySelectorAll('p').forEach((paragraph) => {
		removeEdgeBreaks(paragraph);
	});

	const cleanedHtml = editorRoot ? editorRoot.innerHTML : temp.innerHTML;

	// Check whether the cleaned content is effectively empty
	const check = document.createElement('div');
	check.innerHTML = cleanedHtml;

	const text = check.textContent?.replace(/\u00A0/g, ' ').trim() ?? '';
	const hasMeaningfulElements = !!check.querySelector('img, table, ul, ol, li');

	if (!text && !hasMeaningfulElements) {
		return '';
	}

	return cleanedHtml;
}

function removeEdgeBreaks(paragraph: HTMLElement): void {
	const isWhitespace = (node: Node): boolean => node.nodeType === Node.TEXT_NODE && !node.textContent?.trim();

	const getFirstMeaningfulNode = (): Node | undefined =>
		Array.from(paragraph.childNodes).find((node) => !isWhitespace(node));

	const getLastMeaningfulNode = (): Node | undefined =>
		Array.from(paragraph.childNodes)
			.reverse()
			.find((node) => !isWhitespace(node));

	const isBreak = (node: Node | undefined): boolean => node?.nodeName === 'BR';

	let first = getFirstMeaningfulNode();

	while (isBreak(first)) {
		first?.parentNode?.removeChild(first);
		first = getFirstMeaningfulNode();
	}

	let last = getLastMeaningfulNode();

	while (isBreak(last)) {
		last?.parentNode?.removeChild(last);
		last = getLastMeaningfulNode();
	}
}
